import { ofType } from 'redux-observable';
import { Observable, of } from 'rxjs';
import { filter, delay, withLatestFrom, switchMap, catchError, map, take, tap } from 'rxjs/operators';

import { NavigationEvents, NavigationActions, isNavigationAction } from 'src/components/NavigationListener/types';
import { isProductType } from 'src/util/isProduct';
import { isProductAction, Product, ProductActions, ProductEvents } from 'src/features/product-store/types';
import { fireGTMEvent } from 'src/features/analytics/hooks/useGtm';

import { HubspotAnalyticEvents, HubspotEvents, HubspotTagManagerEvents, HubspotViewProductGtmEvent } from '../types';
import { isSubscribedUser } from '../is-subscribed-user';

/**
 * Listens for navigation to product pages and logs/prepares GTM events
 * for Hubspot product view tracking.
 */
export function hubspotViewProduct(
    action$: Observable<NavigationActions | ProductActions>,
    state$: Observable<any>,
): Observable<any> {
    return action$.pipe(
        ofType(NavigationEvents.NAVIGATION_COMPLETE),

        filter((action) => {
            if (isNavigationAction(action)) {
                return isProductType(action.payload.componentName);
            }

            return false;
        }),

        withLatestFrom(state$),

        filter(([, state]) => isSubscribedUser(state)),

        // Switch to listening for the product view event
        switchMap(() =>
            action$.pipe(
                ofType(ProductEvents.VIEW_PRODUCT),

                filter((action) => isProductAction(action)),

                // Only listern for the first product view event after
                // the navigation complete event. This reduces the risk
                // of sending multiple events for the same product view.
                take(1),

                // Delay the GTM event to ensure it is sent after
                // the page has loaded. This is important for Hubspot
                // to track the event correctly
                delay(5000),

                withLatestFrom(state$),

                switchMap(([viewAction, state]) =>
                    of({ product: viewAction.payload as Product, state }).pipe(
                        // Create the GTM event object
                        map(({ product, state }) => {
                            const cart_id = state.cart?.cartId ?? undefined;
                            const sale_price = product.price?.special_price ?? undefined;
                            const maxPrice = product.price?.maximum_price ?? undefined;

                            let price = maxPrice;
                            let was_price;

                            if (sale_price) {
                                price = sale_price;
                                was_price = maxPrice;
                            }

                            const baseUrl = window?.location?.origin ?? '';
                            let product_url = product.url_key ?? undefined;

                            if (product_url && !product_url?.startsWith('http')) {
                                product_url = baseUrl + '/' + product_url;
                            }

                            let product_image_url = product.small_image ?? undefined;

                            if (product_image_url && !product_image_url.startsWith('http')) {
                                product_image_url = baseUrl + product_image_url;
                            }

                            const labels =
                                product?.labels
                                    ?.map((label) => label.name)
                                    .filter((label): label is string => label !== null && label !== undefined) ?? [];

                            const gtmEvent: HubspotViewProductGtmEvent = {
                                cart_id,
                                currency: state.cart?.raw?.prices?.grand_total?.currency ?? 'EUR',
                                customer_email: state.user?.data?.email,
                                event_name: HubspotAnalyticEvents.VIEW_PRODUCT,
                                event: HubspotTagManagerEvents.VIEW_PRODUCT,
                                is_guest: state.user?.session !== 'IsSignedIn',
                                item_id: product.id,
                                labels,
                                name: product.name,
                                price,
                                product_image_url,
                                product_url,
                                sale_price,
                                save_price: sale_price,
                                session_id: cart_id,
                                sku: product.sku,
                                timestamp: new Date().toISOString(),
                                was_price,
                            };

                            return gtmEvent;
                        }),

                        // Use tap to fire the GTM event as a side effect
                        tap((gtmEvent) => fireGTMEvent(gtmEvent)),

                        // Map to the Redux action to be dispatched
                        map((gtmEvent) => ({
                            type: HubspotEvents.VIEW_PRODUCT,
                            data: gtmEvent,
                        })),
                    ),
                ),
            ),
        ),

        catchError((error) => {
            return of({
                type: 'HubspotEvents.VIEW_PRODUCT_ERROR',
                payload: error,
            });
        }),
    );
}
